import { create } from "zustand"
import { persist } from "zustand/middleware"
import axios from "axios"
import { API_BASE_URL } from "@/lib/axios"
import { fetchProfile } from "@/services/profile"

export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  role: string
  avatar: string | null
  created_at: string
  updated_at: string
}

interface LoginResponse {
  status: number
  message: string
  data: {
    access_token: string
    refresh_token: string
    user: User
  }
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setLoading: (loading: boolean) => void
  clearError: () => void
  getProfile: () => Promise<void>
  setTokens: (accessToken: string, refreshToken: string) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          const { data } = await axios.post<LoginResponse>(
            `${API_BASE_URL}/accounts/login/`,
            { email, password }
          )

          if (data.status !== 1) {
            throw new Error(data.message || "Login failed")
          }

          set({
            user: data.data.user,
            accessToken: data.data.access_token,
            refreshToken: data.data.refresh_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          let message = "Login failed"
          if (axios.isAxiosError(error)) {
            message = error.response?.data?.message || error.message
          } else if (error instanceof Error) {
            message = error.message
          }
          set({ isLoading: false, error: message })
          throw error
        }
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      clearError: () => {
        set({ error: null })
      },

      setTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken })
      },

      getProfile: async () => {
        set({ isLoading: true, error: null })

        try {
          const response = await fetchProfile()

          if (response.status !== 1) {
            throw new Error(response.message || "Failed to fetch profile")
          }

          set({ user: response.data, isLoading: false })
        } catch (error) {
          let message = "Failed to fetch profile"
          if (axios.isAxiosError(error)) {
            message = error.response?.data?.message || error.message
          } else if (error instanceof Error) {
            message = error.message
          }
          set({ isLoading: false, error: message })
          throw error
        }
      },
    }),
    {
      name: "bsok-auth-storage",
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
